import { useMemo, useState } from "react";
import {
  addDays,
  daysBetween,
  formatLongDate,
  formatMonth,
  weekdayIndex,
} from "../../lib/day.ts";
import type { DailyPoint } from "../../lib/types.ts";

const CELL = 13;
const GAP = 3;
const STEP = CELL + GAP;
const TOP = 16;
const LEFT = 22;
const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"];

/** Rampa secuencial validada: 5 tramos de energia, mas claro = menos energia. */
function rampStep(energy: number): number {
  if (energy <= 20) return 1;
  if (energy <= 40) return 2;
  if (energy <= 60) return 3;
  if (energy <= 80) return 4;
  return 5;
}

type Props = { points: DailyPoint[]; from: string; to: string };

/**
 * Calendario de energia.
 *
 * Magnitud sobre una rejilla, asi que color secuencial de un solo tono. Los
 * dias sin registro no son "energia cero": van con un relleno neutro y borde,
 * fuera de la rampa.
 */
export function CalendarHeatmap({ points, from, to }: Props) {
  const [hover, setHover] = useState<string | null>(null);

  const { cells, months, weeks } = useMemo(() => {
    const byDate = new Map(points.map((p) => [p.date, p]));
    // Empieza el lunes de la semana de `from`, para que las filas cuadren.
    const start = addDays(from, -weekdayIndex(from));
    const total = daysBetween(start, to) + 1;

    const cells: Array<{
      date: string;
      week: number;
      day: number;
      point: DailyPoint | undefined;
      inRange: boolean;
    }> = [];
    const months: Array<{ label: string; week: number }> = [];
    let lastMonth = "";
    // Una etiqueta cada pocas semanas como minimo: con meses cortos, dos
    // etiquetas consecutivas se solapaban ("mayjun").
    const MIN_WEEKS_APART = 3;

    for (let i = 0; i < total; i++) {
      const date = addDays(start, i);
      const week = Math.floor(i / 7);
      const day = i % 7;

      if (day === 0) {
        const month = formatMonth(date);
        const previous = months[months.length - 1];
        if (
          month !== lastMonth &&
          (!previous || week - previous.week >= MIN_WEEKS_APART)
        ) {
          months.push({ label: month, week });
        }
        lastMonth = month;
      }

      cells.push({
        date,
        week,
        day,
        point: byDate.get(date),
        inRange: date >= from && date <= to,
      });
    }

    return { cells, months, weeks: Math.ceil(total / 7) };
  }, [points, from, to]);

  const width = LEFT + weeks * STEP;
  const height = TOP + 7 * STEP;
  const active = hover ? cells.find((c) => c.date === hover) : undefined;

  return (
    <figure className="chart">
      <div className="chart__plot chart__plot--scroll">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="chart__svg chart__svg--calendar"
          style={{ minWidth: width }}
          role="img"
          aria-label="Calendario de energía por día"
        >
          {months.map((month) => (
            <text
              key={`${month.label}-${month.week}`}
              x={LEFT + month.week * STEP}
              y={10}
              className="chart__tick"
            >
              {month.label}
            </text>
          ))}

          {WEEKDAYS.map((day, i) =>
            i % 2 === 0 ? (
              <text
                key={day}
                x={LEFT - 6}
                y={TOP + i * STEP + CELL - 3}
                className="chart__tick"
                textAnchor="end"
              >
                {day}
              </text>
            ) : null,
          )}

          {cells.map((cell) => (
            <rect
              key={cell.date}
              x={LEFT + cell.week * STEP}
              y={TOP + cell.day * STEP}
              width={CELL}
              height={CELL}
              rx={3}
              className={
                cell.point
                  ? `cal cal--${rampStep(cell.point.energyAvg)}`
                  : cell.inRange
                    ? "cal cal--empty"
                    : "cal cal--out"
              }
              onPointerEnter={() => cell.inRange && setHover(cell.date)}
              onPointerLeave={() => setHover(null)}
            >
              <title>
                {cell.point
                  ? `${cell.date}: energía ${cell.point.energyAvg}`
                  : `${cell.date}: sin registro`}
              </title>
            </rect>
          ))}
        </svg>
      </div>

      <figcaption className="chart__legend chart__legend--ramp">
        <span className="subtle">Menos</span>
        <span className="cal-key cal--empty" aria-hidden="true" />
        {[1, 2, 3, 4, 5].map((step) => (
          <span key={step} className={`cal-key cal--${step}`} aria-hidden="true" />
        ))}
        <span className="subtle">Más energía</span>
      </figcaption>

      {active && (
        <p className="chart__caption">
          {active.point ? (
            <>
              <strong className="first-cap">
                {formatLongDate(active.date)}
              </strong>{" "}
              · {active.point.mood?.emoji} energía {active.point.energyAvg}
              {active.point.count > 1 ? ` (${active.point.count} registros)` : ""}
            </>
          ) : (
            <span className="muted first-cap">
              {formatLongDate(active.date)} · sin registro
            </span>
          )}
        </p>
      )}
    </figure>
  );
}
